import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { SubscriptionContent } from '@dynamika-shared/types/subscription';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { DotsIcon, TrashIcon, EditIcon } from '@dynamika-shared/ui/icons';
import { Toggle } from '@dynamika-shared/ui/Toggle';
import { Tooltip } from '@dynamika-shared/ui/Tooltip/Tooltip';
import { Avatar } from 'src/components/Avatar/Avatar';
import {
  NotificationRecipientType,
  RECIPIENT_TYPE_LABEL,
} from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/constants';

const ICON_PROPS = { width: 24, height: 24 };

const loadingSkeleton = (width: string) => (
  <div className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`} />
);

const dropdownItems = [
  {
    key: 'edit',
    label: <div>Edit</div>,
    icon: (
      <EditIcon
        width={18}
        height={18}
        className={'ml-[.4rem] pr-[.4rem]'}
      />
    ),
  },
  {
    key: 'delete',
    label: <div className={'text-redOrange'}>Delete</div>,
    icon: <TrashIcon {...ICON_PROPS} className='text-redOrange' />,
  },
];

export function makeSubscriptionColumns(
  onToggleSubscription: (subscription: SubscriptionContent) => void,
  onAction: (action: string, subscription: SubscriptionContent) => void
): ColumnType<SubscriptionContent>[] {
  return [
    {
      name: 'fullName',
      header: () => 'Recipient',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? 'w-[20rem]' : 'w-[17rem]'),
      getValue: row => {
        return (
          <div className='flex items-center gap-2'>
            <div className='flex h-[3.2rem] w-[3.2rem] flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-ghostWhite'>
              <Avatar
                className='text-black bg-brightGray'
                userName={row.fullName ?? ''}
              />
            </div>
            <div className='flex flex-col'>
              <div className='font-sans text-1624 font-semibold text-black'>
                {row.fullName}
              </div>
              <div className='font-nunito text-1216 text-dimGray'>
                {row.channelIdentifier}
              </div>
            </div>
          </div>
        );
      },
      hasSorting: true,
      thClassName: 'w-[28rem] min-w-[28rem]',
    },
    {
      name: 'subscriptionType',
      header: () => 'Recipient Type',
      loading: () => loadingSkeleton('w-[15rem]'),
      getValue: row => (
        <div className='font-sans text-1624 font-medium text-black'>
          {RECIPIENT_TYPE_LABEL[row.subscriptionType as NotificationRecipientType] ?? '-'}
        </div>
      ),
      hasSorting: true,
      thClassName: 'w-[15rem] min-w-[15rem]',
    },
    {
      name: 'scope',
      header: () => 'Scope',
      loading: () => loadingSkeleton('w-[15rem]'),
      getValue: row => {
        const sites =
          row.scope
            ?.split(';')
            .map(site => site.trim())
            .filter(Boolean) || [];
        const isAllSites = sites.length === 0 || sites.includes('all');

        if (isAllSites) {
          return (
            <div className='font-sans text-1624 font-medium text-black'>
              All Sites
            </div>
          );
        }

        const firstSite = sites[0];
        const remainingCount = sites.length - 1;
        const fullText = sites.join(', ');
        const hasMultipleSites = remainingCount > 0;

        const content = (
          <div className='flex items-center gap-2'>
            <div className='max-w-[8rem] truncate font-sans text-1624 font-medium text-black'>
              {firstSite}
            </div>
            {hasMultipleSites && (
              <div className='flex items-center justify-center rounded-[3.2rem] border border-solid border-brightGray bg-white px-2 py-1'>
                <span className='whitespace-nowrap font-sans text-1624 font-medium leading-none text-black'>
                  +{remainingCount}
                </span>
              </div>
            )}
          </div>
        );

        if (!hasMultipleSites) {
          return content;
        }

        return (
          <Tooltip
            when={hasMultipleSites}
            title={<span className='text-black'>{fullText}</span>}
          >
            {content}
          </Tooltip>
        );
      },
      thClassName: 'w-[12rem] min-w-[12rem]',
    },
    {
      name: 'subscribed',
      header: () => 'Subscribed',
      loading: () => (
        <div className='h-[2.4rem] w-[4.2rem] animate-pulse rounded-full bg-brightGray' />
      ),
      getValue: row => (
        <div className='flex justify-center'>
          <Toggle
            checked={row.subscribed}
            onChange={() => onToggleSubscription(row)}
          />
        </div>
      ),
      hasSorting: true,
      thClassName: 'w-[10rem] min-w-[10rem]',
    },
    {
      name: 'action',
      header: () => 'Action',
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray' />
      ),
      getValue: subscription => {
        return (
          <div className='ml-1 flex items-center justify-center'>
            <Dropdown
              items={dropdownItems}
              overlayClassName={'w-[20rem]'}
              trigger={['click']}
              onClick={e => onAction(e.key, subscription)}
            >
              <div
                className='flex h-5 cursor-pointer items-center justify-center'
                data-testid={`menuSubscription-${subscription.subscriptionId}`}
              >
                <div className='rounded-3xl p-1 hover:bg-waterLight'>
                  <DotsIcon className='hover:text-black' />
                </div>
              </div>
            </Dropdown>
          </div>
        );
      },
      alignToRight: true,
      thClassName: 'w-[7rem] min-w-[7rem]',
    },
  ];
}
